import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  CircularProgress,
  Grid,
} from '@mui/material';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
} from 'recharts';
import api from '../../services/api';

const COLORS = ['#0F52BD', '#4CAF50', '#FF9800', '#F44336'];

const Estadisticas = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await api.get('/admin/stats');
        setStats(response.data.data || response.data);
      } catch (err) {
        console.error('Error fetching stats:', err);
        setError('Error al cargar las estadísticas. Inténtalo de nuevo.');
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
  }, []);

  // Prepare chart data
  const registrationData = stats?.registrations_by_month || [];

  const servicesByType = stats?.services?.by_type
    ? Object.entries(stats.services.by_type).map(([name, value]) => ({ name, value }))
    : [];

  const billingByType = stats?.billing?.by_type
    ? Object.entries(stats.billing.by_type).map(([name, value]) => ({ name, value }))
    : [];

  // KPI cards config
  const kpiCards = stats
    ? [
        {
          label: 'Total Clientes',
          value: stats.customers?.total ?? 0,
          color: '#0F52BD',
        },
        {
          label: 'Clientes Activos',
          value: stats.customers?.active ?? 0,
          color: '#4CAF50',
        },
        {
          label: 'Total Servicios',
          value: stats.services?.total ?? 0,
          color: '#FF9800',
        },
        {
          label: 'Servicios Activos',
          value: stats.services?.active ?? 0,
          color: '#0F52BD',
        },
      ]
    : [];

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        maxWidth: '100vw',
        px: { xs: 0, md: 2.5 },
        py: { xs: 0, md: 4 },
        pb: 0,
        mr: { xs: -6 },
        pr: { xs: 0 },
      }}
    >
      {/* Fixed Header */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          width: '100%',
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          padding: { xs: '12px 16px', sm: '8px 16px', md: '12px 16px' },
          boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
          backgroundColor: 'white',
          zIndex: 1000,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            width: '100%',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              flex: 1,
              ml: { xs: '50px', sm: '50px', md: 35.5 },
            }}
          >
            <Typography
              variant="h5"
              sx={{
                fontSize: { xs: '16px', sm: '18px', md: '20px', lg: '22px' },
                fontWeight: 'medium',
                color: '#000',
              }}
            >
              Estadísticas
            </Typography>
            <Typography
              variant="subtitle1"
              sx={{
                fontSize: { xs: '12px', sm: '15px', md: '14px' },
                color: '#666',
                mt: { xs: 0, md: 0.3 },
                display: { xs: 'none', sm: 'none', md: 'none', lg: 'block' },
              }}
            >
              Métricas globales de la plataforma
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Spacer for fixed header */}
      <Box
        sx={{
          height: { xs: '65px', sm: '70px', md: '100px', lg: '90px' },
          '@media (max-width: 600px) and (min-resolution: 1.25dppx)': { height: '65px' },
          '@media (max-width: 600px) and (min-resolution: 1.5dppx)': { height: '90px' },
          '@media (min-width: 600px) and (min-resolution: 1.25dppx)': { height: '75px' },
          '@media (min-width: 900px) and (min-resolution: 1.25dppx)': { height: '95px' },
        }}
      />

      {/* Main content */}
      <Box sx={{ ml: { xs: -6, md: 0 } }}>
        <Box sx={{ paddingLeft: { xs: 6, md: 2 }, paddingRight: { xs: 0, md: 4 } }}>
          {/* Error state */}
          {error && (
            <Box sx={{ mb: 2 }}>
              <Typography sx={{ color: '#c62828', fontSize: '14px' }}>
                {error}
              </Typography>
            </Box>
          )}

          {/* Loading state */}
          {loading ? (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                minHeight: '400px',
              }}
            >
              <CircularProgress sx={{ color: '#0F52BD' }} />
            </Box>
          ) : stats ? (
            <>
              {/* KPI Cards */}
              <Grid container spacing={3} sx={{ mb: 4 }}>
                {kpiCards.map((kpi, index) => (
                  <Grid item xs={12} sm={6} md={3} key={index}>
                    <Paper
                      elevation={0}
                      sx={{
                        p: 3,
                        borderRadius: 2.5,
                        boxShadow: '0px 0px 10px 2px rgba(98, 113, 137, 0.46)',
                        borderTop: `4px solid ${kpi.color}`,
                      }}
                    >
                      <Typography
                        variant="h3"
                        sx={{ fontWeight: 'bold', color: kpi.color }}
                      >
                        {kpi.value}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{ color: '#666', mt: 0.5 }}
                      >
                        {kpi.label}
                      </Typography>
                    </Paper>
                  </Grid>
                ))}
              </Grid>

              {/* Charts section */}
              <Grid container spacing={3} sx={{ mb: 4 }}>
                {/* Bar chart - Registros por mes */}
                <Grid item xs={12} md={7}>
                  <Paper
                    elevation={0}
                    sx={{
                      p: 3,
                      borderRadius: 2.5,
                      boxShadow: '0px 0px 10px 2px rgba(98, 113, 137, 0.46)',
                    }}
                  >
                    <Typography
                      variant="h6"
                      sx={{ fontWeight: 'bold', color: '#000', mb: 3, fontSize: '16px' }}
                    >
                      Registros por mes
                    </Typography>
                    {registrationData.length > 0 ? (
                      <ResponsiveContainer width="100%" height={320}>
                        <BarChart data={registrationData}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#E0E0E0" />
                          <XAxis
                            dataKey="month"
                            tick={{ fontSize: 12, fill: '#666' }}
                            axisLine={{ stroke: '#E0E0E0' }}
                          />
                          <YAxis
                            tick={{ fontSize: 12, fill: '#666' }}
                            axisLine={{ stroke: '#E0E0E0' }}
                            allowDecimals={false}
                          />
                          <Tooltip
                            contentStyle={{
                              borderRadius: '8px',
                              border: '1px solid #E0E0E0',
                              boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                            }}
                          />
                          <Bar
                            dataKey="count"
                            fill="#0F52BD"
                            radius={[4, 4, 0, 0]}
                            name="Registros"
                          />
                        </BarChart>
                      </ResponsiveContainer>
                    ) : (
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'center',
                          alignItems: 'center',
                          height: 320,
                        }}
                      >
                        <Typography sx={{ color: '#666', fontSize: '14px' }}>
                          No hay datos de registros disponibles
                        </Typography>
                      </Box>
                    )}
                  </Paper>
                </Grid>

                {/* Pie chart - Distribución por tipo */}
                <Grid item xs={12} md={5}>
                  <Paper
                    elevation={0}
                    sx={{
                      p: 3,
                      borderRadius: 2.5,
                      boxShadow: '0px 0px 10px 2px rgba(98, 113, 137, 0.46)',
                    }}
                  >
                    <Typography
                      variant="h6"
                      sx={{ fontWeight: 'bold', color: '#000', mb: 3, fontSize: '16px' }}
                    >
                      Distribución por tipo de servicio
                    </Typography>
                    {servicesByType.length > 0 ? (
                      <ResponsiveContainer width="100%" height={320}>
                        <PieChart>
                          <Pie
                            data={servicesByType}
                            cx="50%"
                            cy="45%"
                            innerRadius={60}
                            outerRadius={100}
                            paddingAngle={4}
                            dataKey="value"
                            label={({ name, percent }) =>
                              `${name} (${(percent * 100).toFixed(0)}%)`
                            }
                          >
                            {servicesByType.map((_, index) => (
                              <Cell
                                key={`cell-${index}`}
                                fill={COLORS[index % COLORS.length]}
                              />
                            ))}
                          </Pie>
                          <Tooltip
                            contentStyle={{
                              borderRadius: '8px',
                              border: '1px solid #E0E0E0',
                              boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                            }}
                          />
                          <Legend
                            verticalAlign="bottom"
                            height={36}
                            wrapperStyle={{ fontSize: '13px' }}
                          />
                        </PieChart>
                      </ResponsiveContainer>
                    ) : (
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'center',
                          alignItems: 'center',
                          height: 320,
                        }}
                      >
                        <Typography sx={{ color: '#666', fontSize: '14px' }}>
                          No hay datos de servicios disponibles
                        </Typography>
                      </Box>
                    )}
                  </Paper>
                </Grid>
              </Grid>

              {/* Additional stats section */}
              <Grid container spacing={3} sx={{ mb: 4, alignItems: 'stretch' }}>
                {/* Distribución por tipo de billing */}
                <Grid item xs={12} sm={6} md={4}>
                  <Paper
                    elevation={0}
                    sx={{
                      p: 3,
                      borderRadius: 2.5,
                      boxShadow: '0px 0px 10px 2px rgba(98, 113, 137, 0.46)',
                      height: '100%',
                      boxSizing: 'border-box',
                    }}
                  >
                    <Typography
                      variant="h6"
                      sx={{ fontWeight: 'bold', color: '#000', mb: 2.5, fontSize: '16px' }}
                    >
                      Distribución por tipo de billing
                    </Typography>
                    {billingByType.length > 0 ? (
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                        {billingByType.map((item, index) => {
                          const total = billingByType.reduce((sum, i) => sum + i.value, 0);
                          const percentage = total > 0 ? (item.value / total) * 100 : 0;
                          return (
                            <Box key={item.name}>
                              <Box
                                sx={{
                                  display: 'flex',
                                  justifyContent: 'space-between',
                                  mb: 0.5,
                                }}
                              >
                                <Typography sx={{ fontSize: '14px', color: '#4F4F4F', fontWeight: '500' }}>
                                  {item.name}
                                </Typography>
                                <Typography sx={{ fontSize: '14px', color: '#000', fontWeight: 'bold' }}>
                                  {item.value}
                                </Typography>
                              </Box>
                              <Box
                                sx={{
                                  width: '100%',
                                  height: 8,
                                  backgroundColor: '#f0f0f0',
                                  borderRadius: 4,
                                  overflow: 'hidden',
                                }}
                              >
                                <Box
                                  sx={{
                                    width: `${percentage}%`,
                                    height: '100%',
                                    backgroundColor: COLORS[index % COLORS.length],
                                    borderRadius: 4,
                                    transition: 'width 0.5s ease-in-out',
                                  }}
                                />
                              </Box>
                            </Box>
                          );
                        })}
                      </Box>
                    ) : (
                      <Typography sx={{ color: '#666', fontSize: '14px' }}>
                        No hay datos de billing disponibles
                      </Typography>
                    )}
                  </Paper>
                </Grid>

                {/* Servicios por tipo */}
                <Grid item xs={12} sm={6} md={4}>
                  <Paper
                    elevation={0}
                    sx={{
                      p: 3,
                      borderRadius: 2.5,
                      boxShadow: '0px 0px 10px 2px rgba(98, 113, 137, 0.46)',
                    }}
                  >
                    <Typography
                      variant="h6"
                      sx={{ fontWeight: 'bold', color: '#000', mb: 2.5, fontSize: '16px' }}
                    >
                      Servicios por tipo
                    </Typography>
                    {servicesByType.length > 0 ? (
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        {servicesByType.map((item, index) => (
                          <Paper
                            key={item.name}
                            elevation={0}
                            sx={{
                              p: 2,
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                              backgroundColor: '#fafafa',
                              borderRadius: 2,
                              borderLeft: `4px solid ${COLORS[index % COLORS.length]}`,
                            }}
                          >
                            <Typography sx={{ fontSize: '14px', color: '#4F4F4F', fontWeight: '500' }}>
                              {item.name}
                            </Typography>
                            <Typography
                              sx={{
                                fontSize: '20px',
                                fontWeight: 'bold',
                                color: COLORS[index % COLORS.length],
                              }}
                            >
                              {item.value}
                            </Typography>
                          </Paper>
                        ))}
                      </Box>
                    ) : (
                      <Typography sx={{ color: '#666', fontSize: '14px' }}>
                        No hay datos de servicios disponibles
                      </Typography>
                    )}
                  </Paper>
                </Grid>

                {/* Clientes por tipo */}
                {stats.customers?.by_type && Object.keys(stats.customers.by_type).length > 0 && (
                  <Grid item xs={12} sm={6} md={4}>
                    <Paper
                      elevation={0}
                      sx={{
                        p: 3,
                        borderRadius: 2.5,
                        boxShadow: '0px 0px 10px 2px rgba(98, 113, 137, 0.46)',
                      }}
                    >
                      <Typography
                        variant="h6"
                        sx={{ fontWeight: 'bold', color: '#000', mb: 2.5, fontSize: '16px' }}
                      >
                        Clientes por tipo
                      </Typography>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        {Object.entries(stats.customers.by_type).map(([name, value], index) => (
                          <Paper
                            key={name}
                            elevation={0}
                            sx={{
                              p: 2,
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                              backgroundColor: '#fafafa',
                              borderRadius: 2,
                              borderLeft: `4px solid ${COLORS[index % COLORS.length]}`,
                            }}
                          >
                            <Typography sx={{ fontSize: '14px', color: '#4F4F4F', fontWeight: '500' }}>
                              {name}
                            </Typography>
                            <Typography
                              sx={{
                                fontSize: '20px',
                                fontWeight: 'bold',
                                color: COLORS[index % COLORS.length],
                              }}
                            >
                              {value}
                            </Typography>
                          </Paper>
                        ))}
                      </Box>
                    </Paper>
                  </Grid>
                )}
              </Grid>
            </>
          ) : null}
        </Box>
      </Box>
    </Box>
  );
};

export default Estadisticas;
